//Imports
import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { HasPermission } from "Core/security";

//Hooks
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

//Actions
import { reset, manageSearchLocations } from "./redux/slice";
import { sendGenericExport, clearRedux, savePreference } from "Redux/actions";
import { openModalCreateEditManageLocation } from "./modals/createEditManageLocation/redux/slice";
import { openModalDeleteManageLocation } from "./modals/deleteManageLocation/redux/slice";

//Controls
import {
  Container,
  IconButton,
  MenuComponent,
  Pill,
  SearchInput,
  TableButtons,
  TableView,
  Tooltip,
  useTablePagination
} from "@gpstrackit/gtc-react-controls";

//Components
import { Grid, Icon } from "@mui/material";
import { NotificationManager } from "../../../../components/Notifications";
import MapModal from "Modules/.common/components/MapModal";
import { SidePanelUnitsList } from "Modules/.common/components/SidePanelUnitsList";
import Preferences from "Core/containers/Preferences";

//Styles
import "./locations.scss";

//Modals
import CreateEditManageLocation from "./modals/createEditManageLocation";
import DeleteManageLocation from "./modals/deleteManageLocation";

//Helpers
import { completeColumns, removeAccents } from "Core/data/Helpers";
import { disableItemSelection } from "../../.globals/utils";

const KEY_PREFERENCE = "Manage-LocationsTab-list";
const INITIAL_SORT = { key: "title", order: "asc" };

const LocationsTab = () => {
  //States
  const [selectedItems, setSelectedItems] = useState([]);
  const [dataToMap, setDataToMap] = useState({});
  const [modalUnit, setModalUnit] = useState(null);
  const [preferences, setPreferences] = useState({});
  const [loadPreferences, setLoadPreferences] = useState(true);
  const [loadColumns, setLoadColumns] = useState(false);

  //Table refs
  const locationsTableRef = useRef();

  //Language
  const { messages, locale } = useIntl();

  //Redux
  const authenticatedId = useSelector((state) => state.securityRedux.authenticatedId);
  const { loadingLocations, locations, totalLocations, error } = useSelector(
    (state) => state.manageLocationsTabRedux
  );
  const exportResult = useSelector((state) => state.commomRedux.exportResult);
  const exportName = useSelector((state) => state.commomRedux.exportName);
  const openCreateEditManageLocation = useSelector(
    (state) => state.manageCreateEditManageLocationRedux.open
  );
  const openDeleteManageLocations = useSelector(
    (state) => state.manageDeleteManageLocationsRedux.open
  );
  const dispatch = useDispatch();
  const paginationLocations = useTablePagination({
    dispatch,
    action: manageSearchLocations,
    searchFields: ["title"],
    tableRef: locationsTableRef,
    sendGenericExport,
    exportName: "locations"
  });

  // Permissions
  const user = useSelector((state) => state.securityRedux.user);
  const editablePermissions = HasPermission(user, "", [339]);

  if (error) {
    NotificationManager.error(
      messages["errorMessage"],
      messages["error"],
      3000,
      null,
      null,
      "warning"
    );
    dispatch(reset({ error: null }));
  }

  if (exportResult && exportName === "locations") {
    NotificationManager.info(
      messages["exportInProcessBody"],
      messages["exportInProcessTitle"],
      4000,
      null,
      null,
      "info",
      null
    );
    dispatch(clearRedux("COMMON", { exportResult: null, exportName: null }));
  }

  //Options
  const menuLocationsTabOptions = [messages["edit"], messages["delete"]];

  //Memo
  const currentColumns = useMemo(
    () => [
      {
        key: "title",
        title: messages["manage_locationsTable_locationsTitle"],
        functionString: 'return row?.title || ""',
        show: true,
        export: true,
        sort: true,
        exportValue: (row) => row?.title || "",
        render: (row) => (
          <Grid container alignItems="center">
            <Grid item className="location-title">
              {row?.title.length > 27 ? (
                <Tooltip title={row.title}>
                  <p>{row.title}</p>
                </Tooltip>
              ) : (
                <p>{row.title}</p>
              )}
            </Grid>
            <Grid item>
              {editablePermissions &&
                <MenuComponent
                  disabled={loadingLocations}
                  options={menuLocationsTabOptions}
                  icon="more_vert"
                  className="showInHover"
                  onClick={handleClickMenu}
                  value={row}
                />
              }
            </Grid>
          </Grid>
        )
      },
      {
        key: "address",
        title: messages["manage_locationsTable_address"],
        functionString: 'return row?.address?.address || ""',
        show: true,
        export: true,
        exportValue: (row) => removeAccents(row?.address?.address || ""),
        render: (row) => (
          <Grid container alignItems="center">
            {row?.address?.address && (
              <Grid
                className="location-address"
                item
                onClick={() => {
                  const data = {
                    show: true,
                    title: row.title,
                    isPointerMarker: true,
                    pointers: [
                      {
                        latitude: row.latitude,
                        longitude: row.longitude,
                        label: "Map Point",
                        heading: 0,
                        color: "000",
                        eventName: "no event",
                        draggable: false
                      }
                    ]
                  };
                  setDataToMap(data);
                }}
              >
                <Icon className="iconFlag">room</Icon>
                <p>{row?.address?.address || ""}</p>
              </Grid>
            )}
          </Grid>
        )
      },
      {
        key: "enabledELDRule",
        title: messages["manage_locationsTable_enabledELDRule"],
        functionString: `return row?.enabledELDRule ? "${messages["manage_locationsTable_activated"]}" :  "${messages["manage_locationsTable_deactivated"]}"`,
        show: true,
        export: true,
        exportValue: (row) =>
          row?.enabledELDRule
            ? `${messages["manage_locationsTable_activated"]}`
            : `${messages["manage_locationsTable_deactivated"]}`,
        render: (row) => (
          <Grid container alignItems="center">
            <Grid item>
              <Pill
                clickable={false}
                style={{ width: 110 }}
                item={{
                  label:
                    messages[
                      row.enabledELDRule
                        ? "manage_locationsTable_activated"
                        : "manage_locationsTable_deactivated"
                    ],
                  color: row.enabledELDRule ? "green" : "default"
                }}
              />
            </Grid>
          </Grid>
        )
      },
      {
        key: "timezone",
        title: messages["manage_locationsTable_timezone"],
        functionString: 'return row?.timezone || ""',
        show: true,
        export: true,
        exportValue: (row) => row?.timezone,
        render: (row) => (
          <Grid container alignItems="center">
            <Grid item>
              <p>{row.timezone || ""}</p>
            </Grid>
          </Grid>
        )
      },
      {
        key: "unitsCount",
        title: messages["manage_locationsTable_unitsCount"],
        functionString: 'return row?.unitsCount || 0',
        show: true,
        export: true,
        exportValue: (row) => row?.unitsCount,
        render: (row) => (
          <Grid container alignItems="center">
            <Grid
              item
              style={{ cursor: !row.unitsCount ? "default" : "pointer" }}
              onClick={() => {
                if (!row.unitsCount) return;
                setModalUnit({
                  isVisible: true,
                  id: row.id,
                  numberUnits: row.unitsCount
                });
              }}
            >
              <p style={row.unitsCount > 0 ? { textDecoration: "underline" } : {}}>
                {row.unitsCount}
              </p>
            </Grid>
          </Grid>
        )
      }
    ],
    [locale]
  );

  //Handlers and utils

  const handleClickMenu = (type, location) => {
    if (type === messages["edit"]) {
      dispatch(openModalCreateEditManageLocation(location));
      return;
    }
    if (type === messages["delete"]) {
      if (!location?.id) return;
      dispatch(openModalDeleteManageLocation({ deleteLocations: [location] }));
    }
  };

  const handleDeleteLocations = useCallback(() => {
    dispatch(openModalDeleteManageLocation({ deleteLocations: selectedItems }));
  }, [selectedItems]);

  const savePreferences = useCallback(
    (preferencesArray) => {
      if (!Array.isArray(preferencesArray)) {
        console.error("Expected an array of preferences");
        return;
      }

      let updatedPreferences = { ...preferences } || {};

      preferencesArray.forEach(({ key, value }) => {
        updatedPreferences[key] = value;
      });

      setPreferences(updatedPreferences);

      dispatch(
        savePreference({
          entityName: "User",
          entityId: authenticatedId,
          preferenceKey: KEY_PREFERENCE,
          preferenceValue: updatedPreferences
        })
      );
    },
    [preferences]
  );

  //Effects
  useEffect(() => {
    if (preferences?.columns?.length && !loadColumns) {
      const preferenceMap = new Map(preferences.columns.map((col) => [col.key, col]));

      // Reorder columns based on preferences (keeping `show`)
      const reorderedColumns = preferences.columns
        .map((pref) => {
          const col = currentColumns.find((c) => c.key === pref.key);
          return col ? { ...col, show: pref.show } : null;
        })
        .filter(Boolean);

      // Add new columns (not listed in preferences), with `show: true` by default
      const newColumns = currentColumns
        .filter((col) => !preferenceMap.has(col.key))
        .map((col) => ({ ...col, show: true }));

      const finalColumns = [...reorderedColumns, ...newColumns];

      paginationLocations.setColumns(finalColumns);
      setPreferences({ ...preferences });
      setLoadColumns(true);
    }
  }, [preferences.columns, loadColumns]);

  useEffect(() => {
    if (paginationLocations.columns.length > 0) {
      setPreferences({ ...preferences, columns: paginationLocations.columns });
      setLoadColumns(false);
    }
    paginationLocations.setColumns(currentColumns);
  }, [locale]);

  return (
    <>
      <Container maxWidth={false}>
        <Grid container direction="row" justifyContent="space-between" alignItems="center">
          <Grid item xs={6} sm={2}>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              spacing={1}
            >
              <Grid item style={{ display: "flex" }}>
                {editablePermissions &&
                  <IconButton
                    tooltip={messages["add"]}
                    placement="bottom"
                    variant="secondary"
                    icon="add_box"
                    size="small"
                    isDisabled={loadingLocations}
                    onClick={() => dispatch(openModalCreateEditManageLocation())}
                  />
                }
                {editablePermissions &&
                  <IconButton
                    tooltip={messages["delete"]}
                    placement="bottom"
                    variant="secondary"
                    icon="delete"
                    size="small"
                    onClick={() => handleDeleteLocations()}
                    style={{ opacity: !selectedItems.length && "0.6" }}
                    isDisabled={!selectedItems.length}
                  />
                }
                <TableButtons
                  messages={messages}
                  buttons={["export", "orderColumn"]}
                  {...paginationLocations}
                  onChangeColumns={(columns) => {
                    savePreferences([{ key: "columns", value: columns }]);
                    paginationLocations.setColumns(columns);
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={5} className="mb-10 text-right">
            <SearchInput
              placeholder={messages["manage_locationsTable_search_by_vehicle_location"]}
              searchText={paginationLocations.searchText}
              onSearchText={paginationLocations.onSearchChange}
            />
          </Grid>
        </Grid>
        <Preferences
          preferencesKey={KEY_PREFERENCE}
          onInitialPreferences={(data) => {
            if (!loadPreferences) return;
            setTimeout(() => {
              let sort = data?.sort || INITIAL_SORT; // * Initial sort
              locationsTableRef.current?.setSort?.(sort);
              paginationLocations.onChangeSort(sort);
            }, 100);
            setPreferences(data);
            setLoadPreferences(false);
          }}
        >
          <TableView
            keyColumn="id"
            loading={loadingLocations || loadPreferences}
            topheight={235}
            data={locations}
            disableItemSelection={disableItemSelection(paginationLocations)}
            total={totalLocations}
            exportOrientation="landscape"
            onSelectedItems={(items) => setSelectedItems(items)}
            {...paginationLocations}
            onChangeColumns={(columns) => {
              const newcurrentColumns = paginationLocations?.columns || [];
              const finalsColumns = completeColumns(newcurrentColumns, columns);
              savePreferences([{ key: "columns", value: finalsColumns }]);
              paginationLocations.onChangeColumns(finalsColumns);
            }}
            onChangeSort={(newSort) => {
              savePreferences([{ key: "sort", value: newSort }]);
              paginationLocations.onChangeSort(newSort);
            }}
          />
        </Preferences>
      </Container>
      <MapModal
        hiddenButton
        {...dataToMap}
        hiddenTraffic={true}
        hiddenWeather={true}
        hiddenSearchMap={true}
        hideModal={() => {
          setDataToMap({ show: false });
        }}
        zoom={18}
      />
      <SidePanelUnitsList
        titleHeader={messages["locationsView_units_in_location"]}
        messageNoData={messages["locations_loading_units"]}
        showPanel={modalUnit?.isVisible}
        total={modalUnit?.numberUnits}
        parameterToSearch={"locationId"}
        valueToSearch={modalUnit?.id}
        onClosePanel={() => {
          setModalUnit({
            isVisible: false,
            id: null
          });
        }}
      />
      {openCreateEditManageLocation && (
        <CreateEditManageLocation refresh={locationsTableRef?.current?.refreshTable} />
      )}
      {openDeleteManageLocations && (
        <DeleteManageLocation refresh={() => {
          paginationLocations.onSearchChange("");
        }} />
      )}
    </>
  );
};

export default LocationsTab;
